<template>
    <div class="view-container">
        <div id="snackbar">{{ $t('Copied') }}</div>
        <Modal :title="$t('AddShortcut').toUpperCase()" :showDialog="activeModal === 'ADD'" @close="closeModal()">
            <software-shortcut-modal :current="null" @cancel="closeModal()" @saved="closeModal(true)"/>
        </Modal>
        <Modal :title="$t('EditShortcut').toUpperCase()" :showDialog="activeModal === 'EDIT'" @close="closeModal()">
            <software-shortcut-modal :current="selectedShortcut" @cancel="closeModal()" @saved="closeModal(true)"/>
        </Modal>
        <Modal :showDialog="activeModal === 'DELETE'" @close="closeModal()" :title="$t('DeleteShortcut').toUpperCase()">
            <delete-confirm @cancel="closeModal()" @confirm="deleteShortcutConfirm" :text="actionText"></delete-confirm>
        </Modal>
        <Modal :title="$t('DownloadProgress')" :showDialog="activeModal === 'Progress'" @close="cancelDownload">
            <div class="progress-container vertical" style="min-width: 30vw;">
                <ProgressBar :loaded="bytesReceived" :total="bytesToReceive"/>
                <button class="cancel-button semi-bold" style="margin-top: 12px;" @click="cancelDownload">{{ $t('Cancel') }}</button>
            </div>
        </Modal>

        <div class="horizontal" style="justify-content: space-between; align-items: center;">
            <h3>{{ $t('SoftwareDownloads') }}</h3>
            <Button buttonClass='save-button semi-bold add-shortcut-button' :title="$t('AddShortcut')" @onClick="openModal(null, 'ADD')"/>
        </div>

        <div v-if="loading" class="empty-state">
            <p>{{ $t('Loading') }}</p>
        </div>

        <div v-else-if="shortcuts.length === 0" class="empty-state">
            <p>{{ $t('NoShortcuts') }}</p>
        </div>

        <div v-else class="cards-grid">
            <div v-for="shortcut in shortcuts" :key="shortcut.id" class="shortcut-card vertical">
                <div class="card-actions horizontal">
                    <div v-if="shortcut.hasVersion" class="card-action" :content="$t('CopyLink')" v-tippy="{ theme : 'customTheme', placement : 'top', arrow : true }" @click="copyLink(shortcut)">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M21 8C21 6.34315 19.6569 5 18 5H10C8.34315 5 7 6.34315 7 8V20C7 21.6569 8.34315 23 10 23H18C19.6569 23 21 21.6569 21 20V8ZM19 8C19 7.44772 18.5523 7 18 7H10C9.44772 7 9 7.44772 9 8V20C9 20.5523 9.44772 21 10 21H18C18.5523 21 19 20.5523 19 20V8Z" fill="#346F8B"/><path d="M6 3H16C16.5523 3 17 2.55228 17 2C17 1.44772 16.5523 1 16 1H6C4.34315 1 3 2.34315 3 4V18C3 18.5523 3.44772 19 4 19C4.55228 19 5 18.5523 5 18V4C5 3.44772 5.44772 3 6 3Z" fill="#346F8B"/></svg>
                    </div>
                    <div class="card-action" :content="$t('Edit')" v-tippy="{ theme : 'customTheme', placement : 'top', arrow : true }" @click="openModal(shortcut, 'EDIT')">
                        <svg width="18" height="18" viewBox="0 0 27 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.64951 16.4004L7.64952 16.4003M7.64951 16.4004L7.64951 16.4004L7.64952 16.4003M7.64951 16.4004C7.64952 16.4004 7.64952 16.4003 7.64952 16.4003M7.64951 16.4004L7.64952 16.4003M14.95 7.25429L17.4952 9.61783L10.5666 16.0519L7.66216 16.3496L7.66203 16.3496C7.65971 16.3499 7.65755 16.35 7.65555 16.35L7.96842 13.737L14.95 7.25429ZM18.5296 4.83337L20.1732 6.35962C20.4089 6.57848 20.4091 6.91217 20.1731 7.13143L19.0692 8.15645L16.524 5.79293L17.5573 4.83337C17.8206 4.58888 18.2663 4.58888 18.5296 4.83337Z" stroke="#346F8B" stroke-width="1.3"/></svg>
                    </div>
                    <div class="card-action" :content="$t('Delete')" v-tippy="{ theme : 'customTheme', placement : 'top', arrow : true }" @click="openDeleteModal(shortcut)">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 1.4C3.5 0.626801 4.1268 0 4.9 0H9.1C9.8732 0 10.5 0.626801 10.5 1.4V2.8H11.8928C11.8971 2.79996 11.9014 2.79996 11.9058 2.8H13.3C13.6866 2.8 14 3.1134 14 3.5C14 3.8866 13.6866 4.2 13.3 4.2H12.5518L11.9447 12.6997C11.8923 13.4324 11.2827 14 10.5482 14H3.45178C2.71729 14 2.10767 13.4324 2.05534 12.6997L1.44822 4.2H0.7C0.313401 4.2 0 3.8866 0 3.5C0 3.1134 0.313401 2.8 0.7 2.8H2.09422C2.09856 2.79996 2.1029 2.79996 2.10722 2.8H3.5V1.4ZM4.9 2.8H9.1V1.4H4.9V2.8ZM2.85178 4.2L3.45178 12.6H10.5482L11.1482 4.2H2.85178ZM5.6 5.6C5.9866 5.6 6.3 5.9134 6.3 6.3V10.5C6.3 10.8866 5.9866 11.2 5.6 11.2C5.2134 11.2 4.9 10.8866 4.9 10.5V6.3C4.9 5.9134 5.2134 5.6 5.6 5.6ZM8.4 5.6C8.7866 5.6 9.1 5.9134 9.1 6.3V10.5C9.1 10.8866 8.7866 11.2 8.4 11.2C8.0134 11.2 7.7 10.8866 7.7 10.5V6.3C7.7 5.9134 8.0134 5.6 8.4 5.6Z" fill="#346F8B"/></svg>
                    </div>
                </div>
                <div class="card-icon">
                    <img v-if="shortcut.iconBase64" :src="shortcut.iconBase64" :alt="shortcut.name"/>
                    <div v-else class="card-icon-placeholder">{{ initials(shortcut.name) }}</div>
                </div>
                <p class="card-name semi-bold">{{ shortcut.name }}</p>
                <div class="card-tags horizontal">
                    <span class="tag">{{ shortcut.platform }}</span>
                    <span class="tag" v-if="shortcut.architecture && shortcut.architecture !== 'any'">{{ shortcut.architecture }}</span>
                </div>
                <p v-if="shortcut.description" class="card-description">{{ shortcut.description }}</p>
                <div class="grow"></div>
                <div class="card-version">
                    <span v-if="shortcut.hasVersion" class="version-badge">v{{ shortcut.softwareVersion }}</span>
                    <span v-else class="version-badge unavailable">{{ $t('NoVersionAvailable') }}</span>
                </div>
                <Button
                    v-if="shortcut.hasVersion"
                    buttonClass='save-button semi-bold download-button'
                    :title="$t('Download')"
                    @onClick="downloadVersion(shortcut.versionId, shortcut.fileName, shortcut.fileSize)"/>
                <Button v-else buttonClass='disabled-button semi-bold download-button' :title="$t('Download')"/>
            </div>
        </div>
    </div>
</template>

<script>
import { getSoftwareShortcutCards, getVersionById, deleteSoftwareShortcut } from '@api';
import axios from 'axios';
import Modal from '../../components/Modal.vue';
import Button from '../../components/Button.vue';
import DeleteConfirm from '../../components/DeleteConfirm.vue';
import SoftwareShortcutModal from '../../components/Software/SoftwareShortcutModal.vue';
import ProgressBar from '../../components/ProgressBar.vue';
import { parseProgress } from '../../util/progress';

var context = null;

export default {
    components: { Modal, Button, DeleteConfirm, SoftwareShortcutModal, ProgressBar },
    data() {
        return {
            shortcuts: [],
            loading: true,
            activeModal: null,
            selectedShortcut: null,
            actionText: null,
            // progress
            bytesReceived: 0,
            bytesToReceive: 0,
            downloadController: null
        }
    },
    methods: {
        copyLink(shortcut) {
            navigator.clipboard.writeText(this.$http.defaults.baseURL + `Updater/version/${shortcut.versionId}`);
            var x = document.getElementById("snackbar");
            x.className = "show";
            x.innerHTML = `${this.$t('Copied')} - ${shortcut.name}`;
            setTimeout(function(){ x.className = x.className.replace("show", ""); }, 3000);
        },
        async getData() {
            this.loading = true;
            this.$store.dispatch('set_loading', true);
            try {
                this.shortcuts = await getSoftwareShortcutCards();
            } finally {
                this.loading = false;
                this.$store.dispatch('set_loading', false);
            }
        },
        initials(name) {
            if (!name) {
                return '?';
            }
            return name.trim().substring(0, 2).toUpperCase();
        },
        openModal(shortcut, modal) {
            this.selectedShortcut = shortcut;
            this.activeModal = modal;
        },
        openDeleteModal(shortcut) {
            this.selectedShortcut = shortcut;
            this.actionText = `${this.$t('DeleteShortcut')}: ${shortcut.name}`;
            this.activeModal = 'DELETE';
        },
        async closeModal(isGetData) {
            if (isGetData) {
                await this.getData();
            }
            this.activeModal = null;
            this.selectedShortcut = null;
            this.actionText = null;
        },
        async deleteShortcutConfirm() {
            await deleteSoftwareShortcut(this.selectedShortcut.id);
            this.closeModal(true);
        },
        async downloadVersion(id, fileName, knownSize = 0) {
            this.activeModal = 'Progress';
            this.bytesReceived = 0;
            this.bytesToReceive = 0;
            this.downloadController = new AbortController();

            var followConfig = {
                signal: this.downloadController.signal,
                onDownloadProgress: progressEvent => {
                    const { loaded, total } = parseProgress(progressEvent, knownSize);
                    context.bytesReceived = loaded;
                    context.bytesToReceive = total;
                }
            };

            try {
                var blob = await getVersionById(id, followConfig);
                var a = document.createElement("a");
                document.body.appendChild(a);
                a.style = "display: none";

                var url = window.URL.createObjectURL(blob.data);
                a.href = url;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            } catch (e) {
                if (!axios.isCancel(e)) {
                    console.dir(e);
                }
            } finally {
                this.activeModal = null;
                this.downloadController = null;
            }
        },
        cancelDownload() {
            if (this.downloadController) {
                this.downloadController.abort();
            }
            this.activeModal = null;
        }
    },
    async mounted() {
        context = this;
        await this.getData();
    }
}
</script>

<style scoped>
.cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.shortcut-card {
    position: relative;
    background: #F9F9F9;
    border-radius: 8px;
    box-shadow: 0px 0px 20px rgba(53, 110, 138, 0.18);
    padding: 20px;
    min-height: 240px;
}

.card-actions {
    position: absolute;
    top: 10px;
    right: 10px;
    gap: 8px;
}

.card-action {
    cursor: pointer;
    padding: 4px;
}

.card-icon {
    width: 72px;
    height: 72px;
    margin: 6px auto 14px auto;
    display: flex;
    align-items: center;
    justify-content: center;
}

.card-icon img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.card-icon-placeholder {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    background: #34758E;
    color: white;
    font-weight: 700;
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.card-name {
    text-align: center;
    font-size: 16px;
    margin: 0 0 8px 0;
    color: #272727;
}

.card-tags {
    justify-content: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
}

.tag {
    background: #E6EEF2;
    color: #34758E;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 600;
}

.card-description {
    text-align: center;
    font-size: 12px;
    color: #6b6b6b;
    margin: 0 0 10px 0;
}

.card-version {
    text-align: center;
    margin-bottom: 10px;
}

.version-badge {
    font-size: 13px;
    font-weight: 700;
    color: #2e7d32;
}

.version-badge.unavailable {
    color: #9e9e9e;
    font-weight: 600;
    font-style: italic;
}

.empty-state {
    text-align: center;
    color: #9e9e9e;
    margin-top: 40px;
}

.progress-container {
    margin: 10px 20px;
}

#upload-progress {
    height: 20px;
    border-radius: 5px;
}
</style>

<style>
.add-shortcut-button {
    background: #34758E;
    color: white;
    border-radius: 4px;
    padding: 8px 18px;
    cursor: pointer;
}

.download-button {
    background: #34758E;
    color: white;
    border-radius: 4px;
    padding: 8px 0;
    width: 100%;
    cursor: pointer;
}

.disabled-button {
    background: #cfcfcf;
    color: #ffffff;
    border-radius: 4px;
    padding: 8px 0;
    width: 100%;
    cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/views/Software/SoftwareShortcuts.vue